import { marked } from 'marked';

/**
 * Simple front matter stripper that doesn't require gray-matter
 * Removes YAML front matter from markdown content
 */
const stripFrontMatter = (content: string): string => {
  // Check if content starts with YAML front matter (---)
  if (content.trim().startsWith('---')) {
    const lines = content.split('\n');
    let endIndex = -1;
    
    // Find the closing --- (skip the first line which is opening ---)
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim() === '---') {
        endIndex = i;
        break;
      }
    }
    
    // If we found the closing ---, return content after it
    if (endIndex > 0) {
      return lines.slice(endIndex + 1).join('\n').trim();
    }
  }
  
  // If no front matter found, return original content
  return content;
};

/**
 * Fetches and parses a markdown file from the public directory
 * @param markdownFile - Path to the markdown file relative to public/blog/
 * @returns Promise that resolves to the parsed HTML content
 */
export const fetchMarkdownContent = async (markdownFile: string): Promise<string> => {
  try {
    const response = await fetch(`/blog/${markdownFile}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch markdown file: ${markdownFile}`);
    }
    
    const rawContent = await response.text();
    
    // Strip front matter without using gray-matter to avoid webpack issues
    const markdownContent = stripFrontMatter(rawContent);
    
    // Convert markdown to HTML
    const htmlContent = marked.parse ? marked.parse(markdownContent) : marked(markdownContent);
    
    return htmlContent as string;
  } catch (error) {
    console.error('Error fetching markdown content:', error);
    throw new Error(`Could not load blog post content from ${markdownFile}`);
  }
};